// src/components/ProtectedRoute.jsx
import { Navigate, useLocation } from "react-router-dom";
import { useUser } from "../context/UserContext";
import NoAccess from "../pages/NoAccess";

export default function ProtectedRoute({
  children,
  roles = [],           // bv: ["admin", "projectleider"]
  allowedRoles = [],    // backwards compat met bestaande routes
  permissions = [],     // optioneel, later uitbreidbaar
}) {
  const { user, loading } = useUser();
  const location = useLocation();

  // 🔄 Wachten tot user geladen is
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="p-4 rounded shadow bg-white">
          Laden...
        </div>
      </div>
    );
  }

  // ❌ Niet ingelogd
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const pathname = String(location.pathname || "");
  const forcePasswordReset = user?.forcePasswordReset === true;
  if (forcePasswordReset && pathname !== "/wachtwoord-reset") {
    return <Navigate to="/wachtwoord-reset" replace />;
  }

  const accountType = String(user?.accountType || "internal").trim().toLowerCase();
  const isCustomerPortalUser = accountType === "customer_portal";
  const isPortalRoute =
    pathname.startsWith("/portaal") || pathname.startsWith("/opdrachtgeversportaal");
  if (isCustomerPortalUser && !isPortalRoute && pathname !== "/wachtwoord-reset") {
    return <Navigate to="/portaal" replace />;
  }

  const roleNames = getRoleNames(user);
  const userRole = user.rol || user.role || roleNames[0] || "";

  const userPermissions = user.permissions || [];

  // 🔐 Rolcontrole
  const requiredRoles =
    Array.isArray(allowedRoles) && allowedRoles.length > 0 ? allowedRoles : roles;

  const normalizedRequiredRoles = requiredRoles.map((role) =>
    String(role || "").trim().toLowerCase()
  );
  const effectiveRoleNames = expandRoleAliases(roleNames);
  const roleAllowed =
    normalizedRequiredRoles.length === 0 ||
    effectiveRoleNames.some((role) => normalizedRequiredRoles.includes(role));

  // 🔐 Permissiecontrole (optioneel)
  const permissionAllowed =
    permissions.length === 0 ||
    permissions.every((p) => userPermissions.includes(p));

  // ❌ Geen toegang
  if (!roleAllowed || !permissionAllowed) {
    const visibleRole = roleNames[0] || userRole || "";
    return (
        <NoAccess
          allowedRoles={requiredRoles}
          currentRole={visibleRole}
          // backwards-compat props
          requiredRoles={requiredRoles}
          requiredPermissions={permissions}
          userRole={visibleRole}
        />
    );
  }

  // ✅ Toegang toegestaan
  return children;
}

function getRoleNames(user) {
  const roleSet = new Set();
  if (user?.rol) roleSet.add(String(user.rol).trim().toLowerCase());
  if (user?.role) roleSet.add(String(user.role).trim().toLowerCase());

  for (const role of user?.roles || []) {
    if (!role) continue;
    if (typeof role === "string") {
      roleSet.add(role.trim().toLowerCase());
      continue;
    }
    if (typeof role === "object") {
      const name = role.name || role.naam;
      if (name) roleSet.add(String(name).trim().toLowerCase());
    }
  }

  return Array.from(roleSet);
}

function expandRoleAliases(roleNames = []) {
  const normalized = (Array.isArray(roleNames) ? roleNames : [])
    .map((role) => String(role || "").trim().toLowerCase())
    .filter(Boolean);

  const set = new Set(normalized);
  if (set.has("zzp_owner")) {
    set.add("tenantadmin");
    set.add("projectleider");
  }

  return Array.from(set);
}
